import {
  TRAINING_VIDEO_FORMAT_LABEL,
  TRAINING_VIDEO_MIME,
  formatBytes,
  videoTooLargeMessage,
  videoTooLongMessage,
  BYTES_PER_MB,
  type TrainingVideoSettings,
  type TrainingVideoUsageDto,
} from '@ukinfo/shared';

/**
 * Vorabpruefung von Trainingsvideos im Browser.
 *
 * Der Server prueft alles noch einmal (Format, Groesse beim Empfang, Kontingent);
 * hier geht es darum, einem Trainer am Handy vor einem langen Upload zu sagen, was
 * nicht passt - verstaendlich und mit einem Ausweg. Die Laenge kennt nur der
 * Browser: ein unsichtbares <video>-Element liest sie aus den Metadaten.
 */

/** Dateiauswahl: Videos der drei erlaubten Formate; die Endungen helfen Browsern ohne Typangabe. */
export const VIDEO_FILE_ACCEPT = 'video/mp4,video/webm,video/quicktime,.mp4,.m4v,.webm,.mov';

export interface VideoMeta {
  durationSeconds: number;
  width: number;
  height: number;
}

export type VideoCheck = { ok: true } | { ok: false; message: string };

const BY_EXTENSION: Record<string, string> = { mp4: 'video/mp4', m4v: 'video/mp4', webm: 'video/webm', mov: 'video/quicktime' };

/** Dateityp laut Browser; fehlt er (manche Geraete), gilt die Endung. Leer = nicht erlaubt. */
export function videoMime(file: Pick<File, 'type' | 'name'>): string {
  const declared = file.type.toLowerCase();
  if ((TRAINING_VIDEO_MIME as readonly string[]).includes(declared)) return declared;
  if (declared !== '' && declared !== 'application/octet-stream') return '';
  const extension = file.name.toLowerCase().split('.').pop() ?? '';
  return BY_EXTENSION[extension] ?? '';
}

/** Tipp, der bei Problemen mit Groesse oder Format hilft - vor allem am iPhone. */
export const VIDEO_RECORDING_TIP =
  'Tipp für das iPhone: Einstellungen → Kamera → Formate → „Maximal kompatibel“, und beim Aufnehmen 720p wählen.';

/**
 * Prueft Typ, Groesse, freien Speicher und - wenn bekannt - die Laenge. Die Reihenfolge ist die der
 * Hilfe: erst, was sich nicht reparieren laesst (Format), dann was sich kuerzen laesst.
 */
export function checkVideo(
  file: Pick<File, 'type' | 'name' | 'size'>,
  settings: TrainingVideoSettings,
  usage: Pick<TrainingVideoUsageDto, 'usedBytes' | 'quotaBytes'> | null,
  meta: VideoMeta | null,
): VideoCheck {
  if (videoMime(file) === '') {
    return { ok: false, message: `Dieses Format wird nicht unterstützt. Erlaubt sind ${TRAINING_VIDEO_FORMAT_LABEL}.` };
  }
  if (file.size === 0) return { ok: false, message: 'Die Datei ist leer.' };
  if (file.size > settings.maxSizeMb * BYTES_PER_MB) {
    return { ok: false, message: videoTooLargeMessage(settings.maxSizeMb, file.size) };
  }
  if (usage && file.size > Math.max(0, usage.quotaBytes - usage.usedBytes)) {
    const free = Math.max(0, usage.quotaBytes - usage.usedBytes);
    return {
      ok: false,
      message: `Der Speicher für Trainingsvideos hat nur noch ${formatBytes(free)} frei, das Video ist ${formatBytes(file.size)} groß. Bitte alte Videos löschen oder das Kontingent in den Einstellungen erhöhen.`,
    };
  }
  if (meta && meta.durationSeconds > settings.maxDurationSeconds) {
    return { ok: false, message: videoTooLongMessage(settings.maxDurationSeconds, meta.durationSeconds) };
  }
  return { ok: true };
}

/**
 * Liest Dauer und Aufloesung aus den Metadaten der Datei. Gibt null zurueck, wenn der Browser sie nicht
 * liest (Format, Zeitueberschreitung): die Laenge ist dann unbekannt, hochgeladen werden darf trotzdem -
 * Groesse und Format sind ohnehin geprueft.
 */
export function readVideoMetadata(file: Blob, timeoutMs = 8000): Promise<VideoMeta | null> {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    // Unsichtbar, aber im Dokument: manche Browser laden Metadaten fuer ein abgehaengtes Element nicht.
    video.hidden = true;
    video.preload = 'metadata';
    video.muted = true;
    video.setAttribute('playsinline', '');
    const url = URL.createObjectURL(file);
    let finished = false;
    const finish = (meta: VideoMeta | null): void => {
      if (finished) return;
      finished = true;
      window.clearTimeout(timer);
      video.removeAttribute('src');
      video.load?.();
      video.remove();
      URL.revokeObjectURL(url);
      resolve(meta);
    };
    const timer = window.setTimeout(() => finish(null), timeoutMs);
    video.addEventListener('loadedmetadata', () => {
      const seconds = video.duration;
      finish(Number.isFinite(seconds) && seconds > 0 ? { durationSeconds: seconds, width: video.videoWidth, height: video.videoHeight } : null);
    }, { once: true });
    video.addEventListener('error', () => finish(null), { once: true });
    document.body.appendChild(video);
    video.src = url;
  });
}

/** "1:35" oder "0:08": Laenge fuer die Anzeige. */
export function clock(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/** "training.mov · 38 MB · 0:42 · 1280 × 720" */
export function videoSummary(file: Pick<File, 'name' | 'size'>, meta: VideoMeta | null): string {
  return [
    file.name,
    formatBytes(file.size),
    meta ? clock(meta.durationSeconds) : null,
    meta && meta.width > 0 ? `${meta.width} × ${meta.height}` : null,
  ].filter(Boolean).join(' · ');
}
